import { useCallback, useEffect } from 'react';
import { useMetadataUpdate, type MetadataUpdateEvent } from '@dev-streetbeat/chat/react';
import chatStore from 'shared/store/chatStore';
import activityPanelStore from 'shared/store/activityPanelStore';
import constraintsStore from 'shared/store/constraintsStore';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';
import { ProductDisplayRequestItem } from 'types/ProductTypes';
import { ConstraintDocument } from 'types/ConstraintProfileTypes';
import { CONSTRAINTS } from 'pages/assistant/components/activity-panel/panels/constraints/constants';
import { planAppliedProfileReconciliation } from 'pages/assistant/components/activity-panel/panels/constraints/lifecycle/reconcileFromMetadata';
import { panelSectionForGroup } from 'pages/assistant/components/activity-panel/panels/constraints/panelSections';
import { cloneDocument, diffDocumentPaths, diffMicrocategoryPaths, diffProductPaths, documentsEqual } from 'utils/constraintDocument';

/**
 * Hook to detect agent-pushed constraint changes from the SDK's
 * `metadata.updated` channel and surface them to the UI as red dots +
 * orange field borders.
 *
 * Why the SDK signal specifically:
 *
 * The backend emits two metadata-bearing event families. `metadata.updated`
 * fires ONLY when the agent runtime mutates thread metadata mid-run (via
 * `on_set_metadata()` in ace-backend). `thread.updated` /
 * `thread.connected` / `thread.created` fire on full state reloads,
 * including the FE's own `seedThread` echoes. Subscribing to the
 * `metadata.updated` channel via `useMetadataUpdate` thus gives us a
 * source that is intrinsically agent-only — no echo-suppression
 * heuristic needed on this side.
 *
 * The handler computes the field-level diff against `event.previous`
 * (the merged `threadMetadata` snapshot AS OF JUST BEFORE this delta
 * was applied). That is the only place the diff baseline can live —
 * if we read `threadMetadata` from the SDK context AFTER the handler
 * returned, we'd race the merge.
 */
export const useConstraintsChangeDetection = () => {
  const threadMetadata = chatStore((s) => s.threadMetadata);
  // Anchor "what thread did we last reconcile" in the global store. The hook
  // is remounted (with fresh refs) on every thread swap because
  // ChatPanelWithProvider remounts the SDK provider, so a useRef-based
  // anchor cannot tell "remount on a different thread" apart from "thread
  // creation" — both look like `prev === undefined`. The global anchor
  // survives the remount and lets the cleanup branch fire correctly.
  const metadataThreadId = chatStore((s) => s.metadataThreadId);
  const lastReconciledMetadataThreadId = chatStore((s) => s.lastReconciledMetadataThreadId);

  // Reset per-thread state the FIRST time we observe a new thread's metadata.
  // Covers both fresh page loads on a thread URL and in-app navigation. On a
  // remount, the prior thread's `workingDocument` is still in the global
  // constraintsStore; without resetting it, the next `applyAgentDelta`
  // would diff against the prior thread's data and surface every differing
  // field as an agent change (random red dots across the panel that persist
  // even after navigating back).
  //
  // EXCEPTION: thread creation. On createThreadAndStart, metadataThreadId
  // transitions null → newId — same shape as navigation — but the metadata
  // is OUR write (the workingDocument that was on /assistant). Wiping
  // workingDocument here would null it out just before the main effect
  // hydrates it from the same incoming metadata — visible as a flicker
  // back to Predefiniti. `syncInFlight` is the signal that the metadata
  // arrival belongs to a write WE just initiated, not a thread navigation
  // — skip cleanup in that case but still advance
  // lastReconciledMetadataThreadId so subsequent agent-driven updates
  // don't re-trigger this branch.
  useEffect(() => {
    if (!metadataThreadId) return;
    if (metadataThreadId === lastReconciledMetadataThreadId) return;

    const isOurOwnWrite = constraintsStore.getState().syncInFlight;
    if (!isOurOwnWrite) {
      constraintsStore.setState({
        workingDocument: null,
        agentChangedPaths: [],
      });
      // Menu-button red dot is derived from constraintsStore
      // (agentChangedPaths + acknowledgedSectionGroups), so clearing
      // those above already turns it off — no explicit toggle here.
      activityPanelStore.setState({
        newConstraintsAdded: false,
      });
    }
    chatStore.getState().setLastReconciledMetadataThreadId(metadataThreadId);
  }, [metadataThreadId, lastReconciledMetadataThreadId]);

  /**
   * Per-delta handler. Fires synchronously inside the SDK's
   * `metadata.updated` plumbing BEFORE the merge into the SDK's
   * `threadMetadata` snapshot. `event.previous` is the pre-merge
   * snapshot we diff against to compute changed paths.
   *
   * Side-effects:
   *   - Updates `workingDocument` to the agent's new view.
   *   - Adds field-level diff paths to `agentChangedPaths` (orange
   *     borders, per-section dots).
   *   - Triggers the 500 ms delayed `newConstraintsAdded` pulse on the
   *     activity panel. The menu-button red dot itself is derived from
   *     `agentChangedPaths` + `acknowledgedSectionGroups`, so adding paths
   *     above already flips it on.
   *   - Skips the indicator-raising steps during the automated
   *     rebalancing/diagnosis import (before the user has sent any
   *     message), since that import is a baseline-establishing event,
   *     not an advisor-facing change.
   */
  const onMetadataUpdate = useCallback((event: MetadataUpdateEvent) => {
    const newConstraints = event.updates[CONSTRAINTS.METADATA_KEY] as
      | ConstraintDocument
      | undefined;
    if (!newConstraints) {
      // No constraint document in this delta — nothing for us to do.
      return;
    }
    const prevConstraints = (event.previous?.[CONSTRAINTS.METADATA_KEY] ?? null) as
      | ConstraintDocument
      | null;

    const { constraintsPanelMode, userSentFirstMessage } = activityPanelStore.getState();
    const isAutomatedImport =
      (constraintsPanelMode === 'rebalancing' || constraintsPanelMode === 'diagnosis') &&
      !userSentFirstMessage;

    // Compute the field-level diff in the handler so we know whether THIS
    // delta added new paths (i.e. whether the menu dot / pulse should
    // fire). Two parallel diffs:
    //   1. `diffDocumentPaths(..., mode)` — schema-driven SAVABLE_PATHS
    //      cells (risk, indicator, dividend, cost, instrument_type,
    //      core_satellite, top_quality, macro_asset_allocation, macro,
    //      esg, liquidity). Drives the panel's orange field borders.
    //   2. `diffProductPaths` — ISIN-keyed per-product cells
    //      (`product.<section>.<isin>.<field>`). Drives the orange
    //      borders inside the ProductModal. The two diff helpers
    //      cover disjoint regions of the document, so concatenating is
    //      safe.
    // Relying on `getState().agentChangedPaths.length > 0` after the
    // store call would conflate "this delta added paths" with "there
    // are residual unacknowledged paths from earlier deltas".
    const mode = constraintsStore.getState().mode;
    const newlyChangedPaths = prevConstraints
      ? [
          ...diffDocumentPaths(prevConstraints, newConstraints, mode),
          ...diffProductPaths(prevConstraints, newConstraints),
          ...diffMicrocategoryPaths(prevConstraints, newConstraints),
        ]
      : [];

    // Adopt the new doc as the working document. The store action's own
    // diff covers SAVABLE_PATHS only, so we explicitly merge the
    // product paths in afterwards to keep both surfaces aligned.
    constraintsStore.getState().applyAgentDelta(newConstraints, prevConstraints);
    if (newlyChangedPaths.length > 0 && prevConstraints) {
      const merged = Array.from(
        new Set([
          ...constraintsStore.getState().agentChangedPaths,
          ...newlyChangedPaths,
        ]),
      );
      // Section-dot relight policy at metadata.updated time, applied
      // uniformly to every section (product included):
      //   - If the section's accordion is currently OPEN, the user is
      //     looking at the inputs — suppress the new dot by ADDING the
      //     touched group to acknowledgedSectionGroups (which
      //     `sectionHasAgentChange` reads to hide the dot).
      //   - If the section is CLOSED, surface the dot so the user
      //     knows there's something new — remove the group from
      //     acknowledgedSectionGroups so a previously-acknowledged
      //     section relights for the new delta.
      const touchedGroups = new Set<string>();
      for (const path of newlyChangedPaths) {
        const firstDot = path.indexOf('.');
        if (firstDot > 0) touchedGroups.add(path.slice(0, firstDot));
      }
      const threadIdForOpenLookup = chatStore.getState().metadataThreadId ?? '';
      const openedSections = new Set(
        activityPanelStore.getState().getOpenedConstraints(threadIdForOpenLookup),
      );
      const ackSet = new Set(constraintsStore.getState().acknowledgedSectionGroups);
      touchedGroups.forEach((g) => {
        const section = panelSectionForGroup(g);
        if (openedSections.has(section)) {
          ackSet.add(g);
        } else {
          ackSet.delete(g);
        }
      });
      constraintsStore.setState({
        agentChangedPaths: merged,
        acknowledgedSectionGroups: Array.from(ackSet),
      });
    }

    if (isAutomatedImport) {
      // Baseline import — clear any paths the delta might have surfaced
      // and leave the menu dot off.
      constraintsStore.setState({ agentChangedPaths: [] });
      return;
    }

    if (newlyChangedPaths.length === 0) {
      // No new paths from this delta — nothing to flag. The menu dot is
      // derived from constraintsStore (agentChangedPaths +
      // acknowledgedSectionGroups), so any earlier dot persists naturally
      // until acknowledged.
      return;
    }

    // The menu-button red dot is no longer toggled here — it's derived
    // from the same store fields the section dots use, so adding paths to
    // `agentChangedPaths` above already flips it on.
    setTimeout(() => {
      activityPanelStore.setState({ newConstraintsAdded: true });
    }, 500);
  }, []);

  useMetadataUpdate(onMetadataUpdate);

  // Hydrate workingDocument from the merged threadMetadata snapshot.
  // Runs on every metadata change — `thread.connected` (page reload /
  // reconnect), `thread.updated` (FE's own seedThread echo), and
  // `metadata.updated` (agent delta). For agent deltas the
  // useMetadataUpdate handler has already set workingDocument
  // synchronously, so the equality guard makes this a no-op; for
  // thread.connected / thread.updated this is the only path that
  // populates workingDocument. Indicator firing (red dot, orange
  // borders) lives in the useMetadataUpdate handler above and stays
  // OUT of this effect — page reload should NOT raise indicators,
  // even when the merged metadata is non-trivial.
  useEffect(() => {
    const currentConstraints = threadMetadata?.[CONSTRAINTS.METADATA_KEY] as
      | ConstraintDocument
      | undefined;
    if (!currentConstraints) return;
    const wd = constraintsStore.getState().workingDocument;
    if (wd && documentsEqual(wd, currentConstraints)) return;
    constraintsStore.setState({
      workingDocument: cloneDocument(currentConstraints),
    });
  }, [threadMetadata]);

  // Fetch display data for any products in the latest constraints metadata.
  // Decoupled from the delta handler because the metadata snapshot is the
  // authoritative shape — the delta might only carry a subset, but we want
  // to (re-)hydrate the display data whenever the merged snapshot's
  // products list changes.
  useEffect(() => {
    const currentConstraintsMetadata = threadMetadata?.[CONSTRAINTS.METADATA_KEY] as
      | ConstraintDocument
      | undefined;
    if (!currentConstraintsMetadata) return;

    const productData = currentConstraintsMetadata.product as
      | Record<string, Record<string, { investment_type?: string | null }>>
      | undefined;
    if (!productData) return;

    const allItems: ProductDisplayRequestItem[] = [
      ...Object.keys(productData.individual_products ?? {}).map((isin) => ({
        isin,
        investment_type: null as string | null,
      })),
      ...Object.keys(productData.list_products ?? {}).map((isin) => ({
        isin,
        investment_type: null as string | null,
      })),
      ...Object.keys(productData.portfolio_products ?? {}).map((isin) => ({
        isin,
        investment_type: productData.portfolio_products?.[isin]?.investment_type ?? null,
      })),
    ];
    if (allItems.length > 0) {
      activityPanelStore.getState().fetchProductDisplayData(allItems);
    }
  }, [threadMetadata]);

  // Sync portfolio_product_values from metadata to store
  useEffect(() => {
    if (threadMetadata && threadMetadata[CONSTRAINTS.PORTFOLIO_PRODUCT_VALUES_KEY]) {
      activityPanelStore.getState().setPortfolioProductValues(threadMetadata[CONSTRAINTS.PORTFOLIO_PRODUCT_VALUES_KEY]);
    }
  }, [threadMetadata]);

  // Single reconciliation point for the applied-profile reference. The
  // planner (`planAppliedProfileReconciliation`) is pure — given the
  // current metadata + catalog + mode + local state, it returns ONE action.
  // The hook just translates that action into a single store call. No
  // ordering between branches, no per-branch gating: the decision is
  // captured once and applied transactionally.
  //
  // Why subscribe to `syncInFlight` as a dep: the cleanup branch
  // (`replace_with_predefiniti`) sets pendingSync to trigger a silent sync
  // that overwrites the dead reference. If a prior write is still in
  // flight, stacking another silent sync on top of it would queue two
  // writes for the same thread. We defer; when the in-flight write
  // resolves and clears the flag, this effect re-runs and the cleanup
  // proceeds.
  const sessionLoaded = constraintProfilesStore((s) => s.loaded);
  const syncInFlight = constraintsStore((s) => s.syncInFlight);
  useEffect(() => {
    if (!sessionLoaded) return;
    const profilesByMode = constraintProfilesStore.getState().profilesByMode;
    const mode = constraintsStore.getState().mode;
    const currentAppliedProfile = constraintsStore.getState().appliedProfile;

    const action = planAppliedProfileReconciliation({
      metadata: threadMetadata,
      profilesByMode,
      mode,
      currentAppliedProfile,
    });

    switch (action.kind) {
      case 'noop':
        return;
      case 'restore':
        constraintsStore.getState().restoreAppliedProfile(action.profile);
        return;
      case 'clear':
        constraintsStore.setState({ appliedProfile: null });
        return;
      case 'replace_with_predefiniti':
        if (syncInFlight) return;
        constraintsStore.getState().replaceAppliedProfile(null);
        return;
    }
  }, [threadMetadata, sessionLoaded, syncInFlight]);
};
